// ColourToggle — a chip that opens a panel for choosing the cook-screen
// colour scheme. Sits next to the handedness toggle in the cooking top
// bars. Selecting a scheme calls `onChange`; the store swaps the active
// palette and the cook screen re-themes on the next render.
//
// The chip and panel themselves use the *current* theme; each option shows
// a little swatch strip drawn from that scheme's palette so the choice is
// visible at a glance. The outstanding panel stays black/white regardless.

import { useEffect, useRef, useState } from 'react';
import { theme } from '../theme';
import { SCHEME_LABELS, SCHEME_ORDER, type SchemeName } from '../theme';
import { palettes } from '../tokens';

export interface ColourToggleProps {
  value: SchemeName;
  onChange: (scheme: SchemeName) => void;
  /** Trigger shape. 'pill' (default) for the cooking top bars; 'rectangle'
   *  for the Home footer chip row. */
  variant?: 'pill' | 'rectangle';
  /** Whether the trigger shows the scheme's accent swatch. Default true. */
  swatch?: boolean;
  /** Optional text before the scheme label on the trigger, e.g. 'Colour — '
   *  → 'Colour — Calm'. The aria-label is unchanged. Default ''. */
  prefix?: string;
}

export function ColourToggle({
  value,
  onChange,
  variant = 'pill',
  swatch = true,
  prefix = '',
}: ColourToggleProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  // A single round swatch of the scheme's primary accent colour.
  const Dot = ({ scheme, size }: { scheme: SchemeName; size: number }) => (
    <span
      aria-hidden="true"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '50%',
        background: palettes[scheme].accent.action,
        border: `1px solid ${theme.bg.muted}`,
        flex: '0 0 auto',
      }}
    />
  );

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Colour scheme: ${SCHEME_LABELS[value]}`}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: swatch ? '0.4rem' : 0,
          minHeight: '44px',
          padding: '0 0.9rem',
          background: theme.bg.raised,
          color: theme.text.primary,
          border: `1px solid ${theme.bg.muted}`,
          borderRadius: variant === 'rectangle' ? '8px' : '999px',
          cursor: 'pointer',
          fontSize: '0.85rem',
          fontWeight: 600,
        }}
      >
        {swatch && <Dot scheme={value} size={16} />}
        {prefix}
        {SCHEME_LABELS[value]}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Choose a colour scheme"
          style={{
            position: 'absolute',
            top: 'calc(100% + 0.25rem)',
            right: 0,
            zIndex: 20,
            minWidth: '12rem',
            background: theme.bg.base,
            color: theme.text.primary,
            border: `1px solid ${theme.bg.muted}`,
            borderRadius: '10px',
            boxShadow: '0 6px 24px rgba(0, 0, 0, 0.18)',
            padding: '0.4rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.2rem',
            maxHeight: '60vh',
            overflowY: 'auto',
          }}
        >
          {SCHEME_ORDER.map((scheme) => {
            const selected = scheme === value;
            return (
              <button
                key={scheme}
                type="button"
                aria-pressed={selected}
                onClick={() => {
                  onChange(scheme);
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.6rem',
                  minHeight: '40px',
                  padding: '0 0.6rem',
                  background: selected ? theme.bg.raised : 'transparent',
                  color: theme.text.primary,
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: selected ? 700 : 500,
                  textAlign: 'left',
                }}
              >
                <Dot scheme={scheme} size={20} />
                {SCHEME_LABELS[scheme]}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ColourToggle;
